import React from "react";
import { motion } from "framer-motion";
import { useT } from "../i18n/LanguageContext";

const formatName = (p) => (
  <>{p.name_a}<em className="italic text-[var(--pn-amber)]">{p.name_b}</em>{p.name_c}</>
);

export const ProductSection = ({
  index,
  productKey,
  mediaType = "image",
  mediaPrimary,
  mediaSecondary,
  side = "left",
  bg = "noir",
  testId,
}) => {
  const { t } = useT();
  const p = t.products[productKey];
  const labelPrefix = t.products.labelPrefix;
  const shortNameMap = {
    attimosphera: "Attimosphera",
    picanha: "Picanha & Pata Negra",
    filofumo: `${p.name_a.trim()} ${p.name_b}`,
    salsiccia: `${p.name_a.trim()} ${p.name_b}`,
    whisky: `${p.name_a.trim()}${p.name_b}`,
  };
  const isRight = side === "right";
  const bgClass = bg === "wine" ? "bg-wine" : "bg-noir";

  return (
    <section
      className={`relative section-pad-product overflow-hidden ${bgClass}`}
      data-testid={testId || `product-${index}`}
    >
      <div className="absolute inset-0 grain pointer-events-none" />

      {/* Top divider band */}
      <div className="relative max-w-[1280px] mx-auto mb-12 md:mb-16">
        <div className="flex items-center gap-5">
          <span className="font-display italic text-[var(--pn-amber)] text-[15px] md:text-[17px] tracking-wide">
            0{index}
          </span>
          <span className="h-px flex-shrink-0 w-14 bg-[var(--pn-amber)]/40" />
          <span className="eyebrow !text-[10px] md:!text-[11px]">
            {labelPrefix}{shortNameMap[productKey]}
          </span>
          <span className="h-px flex-1 bg-[var(--pn-amber)]/15" />
        </div>
      </div>

      <div className="relative max-w-[1280px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
        {/* Media */}
        <motion.div
          initial={{ opacity: 0, scale: 1.05, y: 20 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.3, ease: [0.22, 0.61, 0.36, 1] }}
          className={`lg:col-span-7 relative ${isRight ? "lg:order-2" : ""}`}
        >
          {mediaType === "image" && (
            <div className="frame aspect-[4/5] md:aspect-[16/11]">
              <img src={mediaPrimary} alt={shortNameMap[productKey]} className="w-full h-full object-cover kenburns" />
            </div>
          )}

          {mediaType === "duo" && (
            <div className="grid grid-cols-5 gap-3 md:gap-5">
              <div className="frame col-span-3 aspect-[4/5]">
                <img src={mediaPrimary} alt={shortNameMap[productKey]} className="w-full h-full object-cover kenburns" />
              </div>
              <div className="col-span-2 relative">
                <span
                  className="ornament-number absolute right-0 -top-8 md:-top-16 no-select pointer-events-none z-10"
                  data-testid={`product-${index}-ornament`}
                >
                  0{index}
                </span>
                <div className="frame aspect-[3/4] mt-20 md:mt-40">
                  <img src={mediaSecondary} alt="" className="w-full h-full object-cover kenburns" />
                </div>
              </div>
            </div>
          )}

          {mediaType === "composition" && (
            <div className="relative">
              <div className="frame aspect-[4/5] md:aspect-square">
                <img src={mediaPrimary} alt={shortNameMap[productKey]} className="w-full h-full object-cover kenburns" />
              </div>
              {mediaSecondary && (
                <div className="hidden md:block absolute -bottom-12 -right-10 w-1/2 frame aspect-square">
                  <img src={mediaSecondary} alt="" className="w-full h-full object-cover kenburns" />
                </div>
              )}
            </div>
          )}

          {mediaType === "whisky" && (
            <div className="grid grid-cols-5 gap-3 md:gap-5">
              {/* Primary big whisky abstract */}
              <div className="col-span-3 relative aspect-[4/5] rounded-sm overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#3A1E0E] via-[#6B3417] to-[#1A0F08]" />
                <div className="absolute inset-0 whisky-fluid" aria-hidden />
                <div className="absolute inset-0 whisky-fluid-2" aria-hidden />
                <div className="absolute inset-0">
                  <div
                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 rounded-full brace-glow"
                    style={{ background: "radial-gradient(circle, rgba(212,97,42,0.55) 0%, rgba(212,97,42,0.18) 40%, transparent 70%)" }}
                  />
                  <div
                    className="absolute top-1/3 left-1/3 w-36 h-36 rounded-full"
                    style={{ background: "radial-gradient(circle, rgba(224,162,74,0.4) 0%, transparent 60%)" }}
                  />
                </div>
                <div className="absolute inset-0 grain opacity-30" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center px-6">
                    <p className="font-display italic text-[var(--pn-amber)]/85 text-[15px] md:text-[22px] leading-snug tracking-wide drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
                      {p.ornament}
                    </p>
                  </div>
                </div>
                <div className="absolute inset-0 vignette" />
              </div>
              {/* Secondary small whisky abstract — different character: amber splash from top */}
              <div className="col-span-2 relative">
                <span
                  className="ornament-number absolute right-0 -top-8 md:-top-16 no-select pointer-events-none z-10"
                  data-testid={`product-${index}-ornament`}
                >
                  0{index}
                </span>
                <div className="relative aspect-[3/4] mt-20 md:mt-40 rounded-sm overflow-hidden">
                  {/* Reversed base: darker top, warmer bottom */}
                  <div className="absolute inset-0 bg-gradient-to-b from-[#1A0F08] via-[#3A1E0E] to-[#7A3A18]" />
                  {/* Slow vertical drip layer */}
                  <div className="absolute inset-0 whisky-fluid-2" aria-hidden />
                  {/* Amber splash from top */}
                  <div
                    className="absolute -top-10 left-1/2 -translate-x-1/2 w-56 h-56 rounded-full"
                    style={{ background: "radial-gradient(circle, rgba(224,162,74,0.55) 0%, rgba(212,97,42,0.22) 45%, transparent 75%)" }}
                  />
                  {/* Subtle lower-right ember */}
                  <div
                    className="absolute bottom-6 right-4 w-20 h-20 rounded-full"
                    style={{ background: "radial-gradient(circle, rgba(212,97,42,0.5) 0%, transparent 70%)" }}
                  />
                  <div className="absolute inset-0 grain opacity-30" />
                  <div className="absolute inset-0 vignette" />
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Text */}
        <div className={`lg:col-span-5 ${isRight ? "lg:order-1 lg:pr-6" : "lg:pl-6"}`}>
          <motion.h3
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 0.61, 0.36, 1] }}
            className="font-display font-light text-[38px] sm:text-[52px] md:text-[64px] leading-[0.98] tracking-[-0.02em] text-[var(--pn-milk)]"
          >
            {formatName(p)}
          </motion.h3>

          {p.tagline && (
            <motion.p
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.9 }}
              className="mt-3 font-display italic text-[var(--pn-amber)] text-[16px] md:text-[18px]"
            >
              {p.tagline}
            </motion.p>
          )}

          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 1 }}
            className="mt-8 space-y-5"
          >
            <p className="leading-[1.75] font-light text-[17px] md:text-[19px] text-[var(--pn-cream)]/90">
              {p.p1}
            </p>
            <p className="leading-[1.75] font-light text-[15px] md:text-[16.5px] text-[var(--pn-cream)]/65">
              {p.p2}
            </p>
            {p.pull && (
              <p className="mt-7 font-display italic text-[20px] md:text-[24px] leading-[1.4] text-[var(--pn-milk)] pl-5 border-l-2 border-[var(--pn-amber)]/60">
                {p.pull}
              </p>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
